import { getDatabase, ref, get, set, update, query, orderByChild, equalTo } from "firebase/database";
import { app } from "../firebase/config";
import {
  hashPassword,
  verifyPassword,
  generateSecurePassword,
} from "../utils/passwordUtils";

const database = getDatabase(app);

/**
 * Verify email credentials against database
 * @param {string} email - User email
 * @param {string} password - Plain text password
 * @returns {Promise<Object>} - Authentication result
 */
export const verifyEmailCredentials = async (email, password) => {
  try {
    console.log("🔄 Verifying email credentials for:", email);

    if (!email || !password) {
      return {
        success: false,
        error: "Email and password are required",
        user: null,
      };
    }

    // Search for user with matching email in emailAuth
    // Search for user with matching email in emailAuth using Query
    // Requires index on "emailAuth/email"
    const usersRef = ref(database, "users");
    const emailQuery = query(usersRef, orderByChild("emailAuth/email"), equalTo(email));
    const snapshot = await get(emailQuery);

    if (!snapshot.exists()) {
      return {
        success: false,
        error: "Invalid email or password",
        user: null,
      };
    }

    const users = snapshot.val();
    // Since email should be unique, we take the first match
    const userId = Object.keys(users)[0];
    const foundUser = users[userId];

    if (!foundUser || !foundUser.emailAuth) {
      return {
        success: false,
        error: "Invalid email or password",
        user: null,
      };
    }

    // Check if account is active
    if (!foundUser.emailAuth.isActive) {
      return {
        success: false,
        error: "Account is disabled. Please contact administrator.",
        user: null,
      };
    }

    // Verify password
    const isPasswordValid = await verifyPassword(
      password,
      foundUser.emailAuth.passwordHash
    );

    if (!isPasswordValid) {
      return {
        success: false,
        error: "Invalid email or password",
        user: null,
      };
    }

    // Update last login time
    await update(ref(database, `users/${userId}/emailAuth`), {
      lastLogin: Date.now(),
    });

    console.log("✅ Email authentication successful for:", email);
    console.log("✅ User data being returned:", {
      uid: userId,
      email: foundUser.emailAuth.email,
      displayName: foundUser.profile?.displayName || null,
      authProvider: "email",
    });

    return {
      success: true,
      error: null,
      user: {
        uid: userId,
        email: foundUser.emailAuth.email,
        displayName: foundUser.profile?.displayName || null,
        photoURL: foundUser.profile?.photoURL || null,
        authProvider: "email",
        isEmailUser: true,
        isActive: foundUser.emailAuth.isActive,
        ...foundUser,
      },
    };
  } catch (error) {
    console.error("❌ Error verifying email credentials:", error);
    return {
      success: false,
      error: "Authentication failed. Please try again.",
      user: null,
    };
  }
};

/**
 * Create new email user (Admin function)
 * @param {string} email - User email
 * @param {string} password - Plain text password
 * @param {string} displayName - User display name
 * @param {string} adminId - ID of admin creating the user
 * @param {number} membership - Membership level (optional)
 * @returns {Promise<Object>} - Creation result
 */
export const createEmailUser = async (
  email,
  password,
  displayName,
  adminId,
  membership = null
) => {
  try {
    console.log("🔄 Creating email user:", email);

    if (!email || !password || !adminId) {
      return {
        success: false,
        error: "Email, password, and admin ID are required",
        userId: null,
      };
    }

    // Use email as display name if not provided
    const finalDisplayName = displayName || email.split("@")[0];

    // Check for duplicate email
    const isDuplicate = await checkEmailExists(email);
    if (isDuplicate) {
      return {
        success: false,
        error: "Email already exists",
        userId: null,
      };
    }

    // Generate unique user ID
    const userId = generateEmailUserId();

    // Hash password
    const passwordHash = await hashPassword(password);

    // Calculate membership expiry if membership is provided
    let membershipExpiry = null;
    if (membership) {
      const expiryDate = new Date();
      switch (membership) {
        case 1: // 7 days
          expiryDate.setDate(expiryDate.getDate() + 7);
          break;
        case 2: // 30 days
          expiryDate.setDate(expiryDate.getDate() + 30);
          break;
        case 3: // 180 days
          expiryDate.setDate(expiryDate.getDate() + 180);
          break;
        case 4: // 360 days
          expiryDate.setDate(expiryDate.getDate() + 360);
          break;
        default:
          expiryDate.setDate(expiryDate.getDate() + 30);
      }

      // Round up to next hour
      expiryDate.setMinutes(0);
      expiryDate.setSeconds(0);
      expiryDate.setMilliseconds(0);
      expiryDate.setHours(expiryDate.getHours() + 1);

      membershipExpiry = expiryDate.getTime();
    }

    // Prepare user data
    const userData = {
      userID: userId,
      createdAt: Date.now(),
      dailyPostCount: {},
      profile: {
        displayName: finalDisplayName,
        email: email,
        photoURL: null,
        authProvider: "email",
      },
      emailAuth: {
        email: email,
        passwordHash: passwordHash,
        isActive: true,
        lastLogin: null,
        createdBy: adminId,
        createdAt: Date.now(),
      },
    };

    // Add membership if provided
    if (membership && membershipExpiry) {
      userData.membership = membership;
      userData.membershipExpiry = membershipExpiry;
    }

    // Save user to database
    const userRef = ref(database, `users/${userId}`);
    await set(userRef, userData);

    console.log("✅ Email user created successfully:", userId);

    return {
      success: true,
      error: null,
      userId: userId,
      user: userData,
    };
  } catch (error) {
    console.error("❌ Error creating email user:", error);
    return {
      success: false,
      error: "Failed to create user. Please try again.",
      userId: null,
    };
  }
};

/**
 * Update email user password
 * @param {string} userId - User ID
 * @param {string} currentPassword - Current plain text password
 * @param {string} newPassword - New plain text password
 * @returns {Promise<Object>} - Update result
 */
export const updateEmailPassword = async (
  userId,
  currentPassword,
  newPassword
) => {
  try {
    console.log("🔄 Updating password for user:", userId);

    if (!userId || !currentPassword || !newPassword) {
      return {
        success: false,
        error: "User ID, current password, and new password are required",
      };
    }

    // Get user data
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);

    if (!snapshot.exists()) {
      return {
        success: false,
        error: "User not found",
      };
    }

    const userData = snapshot.val();

    if (!userData.emailAuth) {
      return {
        success: false,
        error: "User is not an email authentication user",
      };
    }

    // Verify current password
    const isCurrentPasswordValid = await verifyPassword(
      currentPassword,
      userData.emailAuth.passwordHash
    );

    if (!isCurrentPasswordValid) {
      return {
        success: false,
        error: "Current password is incorrect",
      };
    }

    // Hash new password
    const newPasswordHash = await hashPassword(newPassword);

    // Update password in database
    await update(ref(database, `users/${userId}/emailAuth`), {
      passwordHash: newPasswordHash,
      lastPasswordChange: Date.now(),
    });

    console.log("✅ Password updated successfully for user:", userId);

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error("❌ Error updating password:", error);
    return {
      success: false,
      error: "Failed to update password. Please try again.",
    };
  }
};

/**
 * Admin function to reset user password
 * @param {string} userId - User ID
 * @param {string} newPassword - New plain text password (optional, will generate if not provided)
 * @param {string} adminId - ID of admin performing the reset
 * @returns {Promise<Object>} - Reset result
 */
export const adminResetPassword = async (
  userId,
  newPassword = null,
  adminId
) => {
  try {
    console.log("🔄 Admin resetting password for user:", userId);

    if (!userId || !adminId) {
      return {
        success: false,
        error: "User ID and admin ID are required",
        newPassword: null,
      };
    }

    // Get user data
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);

    if (!snapshot.exists()) {
      return {
        success: false,
        error: "User not found",
        newPassword: null,
      };
    }

    const userData = snapshot.val();

    if (!userData.emailAuth) {
      return {
        success: false,
        error: "User is not an email authentication user",
        newPassword: null,
      };
    }

    // Generate password if not provided
    const passwordToUse = newPassword || generateSecurePassword(12);

    // Hash new password
    const newPasswordHash = await hashPassword(passwordToUse);

    // Update password in database
    await update(ref(database, `users/${userId}/emailAuth`), {
      passwordHash: newPasswordHash,
      lastPasswordChange: Date.now(),
      passwordResetBy: adminId,
      passwordResetAt: Date.now(),
    });

    console.log("✅ Password reset successfully by admin for user:", userId);

    return {
      success: true,
      error: null,
      newPassword: passwordToUse,
    };
  } catch (error) {
    console.error("❌ Error resetting password:", error);
    return {
      success: false,
      error: "Failed to reset password. Please try again.",
      newPassword: null,
    };
  }
};

/**
 * Check if email already exists
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} - True if email exists
 */
export const checkEmailExists = async (email) => {
  try {
    const usersRef = ref(database, "users");

    // Check email auth users
    const emailAuthQuery = query(usersRef, orderByChild("emailAuth/email"), equalTo(email));
    const emailAuthSnapshot = await get(emailAuthQuery);

    if (emailAuthSnapshot.exists()) {
      return true;
    }

    // Check Google users (profile.email)
    const googleAuthQuery = query(usersRef, orderByChild("profile/email"), equalTo(email));
    const googleAuthSnapshot = await get(googleAuthQuery);

    if (googleAuthSnapshot.exists()) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("❌ Error checking email existence:", error);
    return false;
  }
};

/**
 * Generate unique user ID for email users
 * @returns {string} - Unique user ID
 */
export const generateEmailUserId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `email_${timestamp}_${randomStr}`;
};

/**
 * Toggle email user account status (Admin function)
 * @param {string} userId - User ID
 * @param {boolean} isActive - New active status
 * @param {string} adminId - ID of admin performing the action
 * @returns {Promise<Object>} - Toggle result
 */
export const toggleEmailUserStatus = async (userId, isActive, adminId) => {
  try {
    console.log(
      `🔄 ${isActive ? "Activating" : "Deactivating"} email user:`,
      userId
    );

    if (!userId || !adminId) {
      return {
        success: false,
        error: "User ID and admin ID are required",
      };
    }

    // Get user data
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);

    if (!snapshot.exists()) {
      return {
        success: false,
        error: "User not found",
      };
    }

    const userData = snapshot.val();

    if (!userData.emailAuth) {
      return {
        success: false,
        error: "User is not an email authentication user",
      };
    }

    // Update user status
    await update(ref(database, `users/${userId}/emailAuth`), {
      isActive: isActive,
      statusChangedBy: adminId,
      statusChangedAt: Date.now(),
    });

    console.log(
      `✅ Email user ${isActive ? "activated" : "deactivated"} successfully:`,
      userId
    );

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error("❌ Error toggling user status:", error);
    return {
      success: false,
      error: "Failed to update user status. Please try again.",
    };
  }
};
